import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowBack,
  Home as HomeIcon,
  Tune as TuneIcon,
  ShoppingCart,
} from "@mui/icons-material";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import { Icon } from "@iconify/react";
import ImageLoader from "components/ImageLoader";
import { LUXURY } from "data/guestLuxuryTheme";

const GUTTER = 20;

const circleBtn = {
  p: 0,
  flexShrink: 0,
  width: 36,
  height: 36,
};

const GuestMenuHeader = ({
  isPublicMode,
  guestMode,
  locationLabel,
  locationSubtitle,
  isRoom,
  companyData,
  onPrimaryNavigation,
  onDesktopBack,
  onOpenCompanyInfo,
  onOpenCart,
  cartItemCount,
  searchQuery,
  onSearchQueryChange,
  onClearSearch,
  activeFilterCount,
  onOpenMobileFilter,
}) => {
  const hotelName = companyData?.name || "Grand Palm Hotel";
  const diningTitle =
    guestMode === "room-service" ? "Room dining" : "Dining";
  const showRoomCard = Boolean(locationLabel);

  const searchField = (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: LUXURY.card,
        borderRadius: "22px",
        px: 1.5,
        height: 52,
        boxShadow: "0 8px 18px rgba(12,42,34,0.04)",
      }}
    >
      <SearchIcon sx={{ fontSize: 20, color: LUXURY.goldDeep, flexShrink: 0 }} />
      <Box
        component="input"
        placeholder="Search dishes..."
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        sx={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          color: LUXURY.ink,
          fontSize: "0.9rem",
          fontFamily: LUXURY.bodyFont,
          "::placeholder": { color: LUXURY.stone },
        }}
      />
      {searchQuery ? (
        <IconButton size="small" onClick={onClearSearch} sx={{ color: LUXURY.hint }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
    </Box>
  );

  const hotelMark = (
    <Box
      onClick={onOpenCompanyInfo}
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.85,
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          color: LUXURY.gold,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {companyData?.logo ? (
          <ImageLoader
            imagePath={companyData.logo}
            width={22}
            height={22}
            alt={hotelName}
            rounded={false}
          />
        ) : (
          <Icon icon="mdi:palm-tree" width={22} height={22} color="currentColor" />
        )}
      </Box>
      <Typography
        sx={{
          minWidth: 0,
          fontFamily: LUXURY.displayFont,
          fontWeight: 600,
          fontSize: "0.68rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: LUXURY.gold,
          lineHeight: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {hotelName}
      </Typography>
    </Box>
  );

  const cartButton = (
    <IconButton
      aria-label="Cart"
      onClick={onOpenCart}
      sx={{
        ...circleBtn,
        bgcolor: LUXURY.forest,
        color: LUXURY.gold,
        boxShadow: "0 8px 16px rgba(8,14,12,0.28)",
        "&:hover": { bgcolor: LUXURY.forestMid },
      }}
    >
      <ShoppingCart sx={{ fontSize: 18 }} />
      {cartItemCount > 0 ? (
        <Box
          sx={{
            position: "absolute",
            top: 7,
            right: 8,
            minWidth: 14,
            height: 14,
            px: 0.35,
            borderRadius: "50%",
            bgcolor: LUXURY.gold,
            color: LUXURY.forestDeep,
            fontSize: "0.55rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {cartItemCount}
        </Box>
      ) : null}
    </IconButton>
  );

  const filterButton = (
    <Box
      component="button"
      type="button"
      aria-label="Filters"
      onClick={onOpenMobileFilter}
      sx={{
        appearance: "none",
        flexShrink: 0,
        width: 52,
        height: 52,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "16px",
        border: 0,
        bgcolor: LUXURY.card,
        color: LUXURY.ink,
        cursor: "pointer",
        position: "relative",
        boxShadow: "0 8px 18px rgba(12,42,34,0.04)",
      }}
    >
      <TuneIcon sx={{ fontSize: 20 }} />
      {activeFilterCount > 0 ? (
        <Box
          sx={{
            position: "absolute",
            top: -4,
            right: -4,
            width: 16,
            height: 16,
            borderRadius: "50%",
            bgcolor: LUXURY.forest,
            color: LUXURY.gold,
            fontSize: "0.6rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {activeFilterCount}
        </Box>
      ) : null}
    </Box>
  );

  const roomCard = showRoomCard ? (
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
        mx: `${GUTTER}px`,
        mt: -2.4,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minHeight: 68,
        px: 1.5,
        py: 1.1,
        borderRadius: "22px",
        bgcolor: LUXURY.card,
        boxShadow: "0 12px 28px rgba(18,28,22,0.12)",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "12px",
          bgcolor: LUXURY.cream,
          color: LUXURY.forest,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {guestMode === "room-service" || isRoom ? (
          <ApartmentRoundedIcon sx={{ fontSize: 20 }} />
        ) : (
          <TableRestaurantRoundedIcon sx={{ fontSize: 20 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "0.95rem",
            color: LUXURY.ink,
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {guestMode === "room-service" ? `Deliver to ${locationLabel}` : locationLabel}
        </Typography>
        {locationSubtitle ? (
          <Typography
            sx={{
              mt: 0.2,
              fontSize: "0.72rem",
              color: LUXURY.hint,
              lineHeight: 1.25,
              wordBreak: "break-word",
            }}
          >
            {locationSubtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        zIndex: 1100,
        flexShrink: 0,
        color: "#fff",
        position: "relative",
        bgcolor: LUXURY.page,
        fontFamily: LUXURY.bodyFont,
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: LUXURY.forestDeep,
          borderBottomLeftRadius: 22,
          borderBottomRightRadius: 22,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${LUXURY.headerImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center 38%",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(8,14,12,0.22) 0%, rgba(8,14,12,0.38) 40%, rgba(8,14,12,0.72) 100%)",
            },
          }}
        />

        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            position: "relative",
            maxWidth: LUXURY.maxWidth,
            mx: "auto",
            px: `${GUTTER}px`,
            pt: "max(14px, env(safe-area-inset-top))",
            pb: showRoomCard ? 6.75 : 1.8,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: 32 }}>
            <IconButton
              aria-label={isPublicMode && !guestMode ? "Home" : "Back"}
              onClick={onPrimaryNavigation}
              sx={{
                ...circleBtn,
                bgcolor: "rgba(255,255,255,0.92)",
                color: LUXURY.forest,
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              {isPublicMode && !guestMode ? (
                <HomeIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowBack sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            {hotelMark}
            {cartButton}
          </Box>

          <Typography
            sx={{
              mt: 0.7,
              fontFamily: LUXURY.displayFont,
              fontWeight: 500,
              fontSize: "1.85rem",
              lineHeight: 1.15,
              color: "#fff",
              textShadow: "0 8px 22px rgba(0,0,0,0.3)",
            }}
          >
            {diningTitle}
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 4,
            gap: 2,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
            {!isPublicMode || guestMode ? (
              <IconButton
                onClick={onDesktopBack}
                sx={{
                  ...circleBtn,
                  width: 40,
                  height: 40,
                  bgcolor: "rgba(255,255,255,0.92)",
                  color: LUXURY.forest,
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                <ArrowBack />
              </IconButton>
            ) : null}
            <Box
              onClick={onOpenCompanyInfo}
              sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", minWidth: 0 }}
            >
              <Box sx={{ color: LUXURY.gold, display: "flex", flexShrink: 0 }}>
                {companyData?.logo ? (
                  <ImageLoader
                    imagePath={companyData.logo}
                    width={28}
                    height={28}
                    alt={hotelName}
                    rounded={false}
                  />
                ) : (
                  <Icon icon="mdi:palm-tree" width={26} height={26} color="currentColor" />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: LUXURY.displayFont,
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    color: LUXURY.gold,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                  }}
                >
                  {hotelName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: LUXURY.displayFont,
                    fontWeight: 500,
                    fontSize: "1.15rem",
                    color: "#fff",
                    lineHeight: 1.15,
                  }}
                >
                  {diningTitle}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, maxWidth: 420 }}>{searchField}</Box>
          </Box>
          <IconButton
            onClick={onOpenCart}
            sx={{
              ...circleBtn,
              width: 44,
              height: 44,
              bgcolor: LUXURY.forest,
              color: LUXURY.gold,
              "&:hover": { bgcolor: LUXURY.forestMid },
            }}
          >
            <ShoppingCart />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {roomCard}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            maxWidth: LUXURY.maxWidth,
            mx: "auto",
            px: `${GUTTER}px`,
            pt: showRoomCard ? 1.25 : 1.5,
            pb: 0.4,
          }}
        >
          {searchField}
          {filterButton}
        </Box>
      </Box>
    </Box>
  );
};

export default GuestMenuHeader;
